import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import { Card, Button, Input, Badge, Textarea } from '../components/ui';
import {
  Star, Users, Image, TrendingUp, Edit, Save, X,
  Instagram, MapPin, Globe, Phone, Palette, Loader2,
  BarChart3, MessageSquare, Camera, CheckCircle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ArtistDashboard = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [artist, setArtist] = useState(null);
  const [stats, setStats] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [profileRes, statsRes, reviewsRes] = await Promise.all([
        fetch(`${API_URL}/api/artist/me`, { credentials: 'include' }),
        fetch(`${API_URL}/api/artist/stats`, { credentials: 'include' }),
        fetch(`${API_URL}/api/artist/reviews`, { credentials: 'include' })
      ]);

      if (profileRes.ok) {
        const data = await profileRes.json();
        setArtist(data);
        setEditForm(data);
      }
      if (statsRes.ok) setStats(await statsRes.json());
      if (reviewsRes.ok) {
        const data = await reviewsRes.json();
        setReviews(data.reviews || []);
      }
    } catch (err) {
      console.error('Failed to fetch artist data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/artist/me`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          name: editForm.name,
          studio_name: editForm.studio_name,
          location: editForm.location,
          instagram: editForm.instagram,
          website: editForm.website,
          phone: editForm.phone,
          styles: editForm.styles,
          bio: editForm.bio
        })
      });
      if (res.ok) {
        const updated = await res.json();
        setArtist(updated);
        setEditing(false);
      }
    } catch (err) {
      console.error('Save failed:', err);
    } finally {
      setSaving(false);
    }
  };

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star key={i} className={`w-4 h-4 ${i < Math.round(rating) ? 'text-primary fill-primary' : 'text-muted-foreground/30'}`} />
    ));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="flex items-center justify-center h-[60vh]">
          <Loader2 className="w-12 h-12 text-primary animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="font-heading text-3xl">
                {t('artist.dashboard', 'Artist Dashboard')}
              </h1>
              <p className="text-muted-foreground mt-1">
                {t('artist.welcome', 'Welcome back')}, {artist?.name || user?.name}
              </p>
            </div>
            <Badge className="gap-1">
              <Palette className="w-3 h-3" />
              {artist?.verified ? t('artist.verified', 'Verified Artist') : t('artist.pending', 'Pending Verification')}
            </Badge>
          </div>
        </motion.div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            {[
              { icon: Star, label: t('artist.rating', 'Rating'), value: stats.average_rating?.toFixed(1) || '0.0', color: 'text-yellow-400' },
              { icon: MessageSquare, label: t('artist.totalReviews', 'Total Reviews'), value: stats.total_reviews, color: 'text-blue-400' },
              { icon: TrendingUp, label: t('artist.recentReviews', 'Recent (30d)'), value: stats.recent_reviews, color: 'text-green-400' },
              { icon: Image, label: t('artist.portfolio', 'Portfolio'), value: stats.portfolio_count, color: 'text-purple-400' },
            ].map((stat, i) => {
              const Icon = stat.icon;
              return (
                <motion.div key={i} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }}>
                  <Card className="p-4">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-sm bg-primary/10 flex items-center justify-center ${stat.color}`}>
                        <Icon className="w-5 h-5" />
                      </div>
                      <div>
                        <p className="text-2xl font-heading">{stat.value}</p>
                        <p className="text-xs text-muted-foreground">{stat.label}</p>
                      </div>
                    </div>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-border">
          {[
            { id: 'overview', label: t('artist.tabOverview', 'Profile'), icon: Users },
            { id: 'reviews', label: t('artist.tabReviews', 'Reviews'), icon: MessageSquare },
            { id: 'portfolio', label: t('artist.tabPortfolio', 'Portfolio'), icon: Camera },
          ].map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-heading border-b-2 transition-colors ${
                  activeTab === tab.id ? 'text-primary border-primary' : 'text-muted-foreground border-transparent hover:text-white'
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Profile Tab */}
        {activeTab === 'overview' && artist && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <Card className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-heading text-xl">{t('artist.profileInfo', 'Profile Information')}</h2>
                {!editing ? (
                  <Button variant="outline" size="sm" onClick={() => setEditing(true)} className="gap-2">
                    <Edit className="w-4 h-4" /> {t('common.edit', 'Edit')}
                  </Button>
                ) : (
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => { setEditing(false); setEditForm(artist); }} className="gap-1">
                      <X className="w-4 h-4" /> {t('common.cancel', 'Cancel')}
                    </Button>
                    <Button size="sm" onClick={handleSave} disabled={saving} className="gap-1">
                      {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                      {t('common.save', 'Save')}
                    </Button>
                  </div>
                )}
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                {[
                  { key: 'name', label: t('artist.name', 'Name'), icon: Users },
                  { key: 'studio_name', label: t('artist.studio', 'Studio'), icon: Palette },
                  { key: 'location', label: t('artist.location', 'Location'), icon: MapPin },
                  { key: 'instagram', label: 'Instagram', icon: Instagram },
                  { key: 'website', label: t('artist.website', 'Website'), icon: Globe },
                  { key: 'phone', label: t('artist.phone', 'Phone'), icon: Phone },
                ].map(field => {
                  const Icon = field.icon;
                  return (
                    <div key={field.key}>
                      <label className="text-xs text-muted-foreground mb-1 block">{field.label}</label>
                      {editing ? (
                        <div className="relative">
                          <Icon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                          <Input
                            value={editForm[field.key] || ''}
                            onChange={(e) => setEditForm({ ...editForm, [field.key]: e.target.value })}
                            className="pl-10"
                          />
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 p-3 bg-background rounded-sm border border-border">
                          <Icon className="w-4 h-4 text-muted-foreground" />
                          <span className="text-sm">{artist[field.key] || '-'}</span>
                        </div>
                      )}
                    </div>
                  );
                })}
                <div className="md:col-span-2">
                  <label className="text-xs text-muted-foreground mb-1 block">{t('artist.styles', 'Styles')}</label>
                  {editing ? (
                    <Input
                      value={Array.isArray(editForm.styles) ? editForm.styles.join(', ') : editForm.styles || ''}
                      onChange={(e) => setEditForm({ ...editForm, styles: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })}
                      placeholder="Realism, Blackwork, Japanese..."
                    />
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {(artist.styles || []).map((style, i) => (
                        <Badge key={i} variant="outline">{style}</Badge>
                      ))}
                      {(!artist.styles || artist.styles.length === 0) && <span className="text-muted-foreground text-sm">-</span>}
                    </div>
                  )}
                </div>
              </div>
            </Card>
          </motion.div>
        )}

        {/* Reviews Tab */}
        {activeTab === 'reviews' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4">
            {reviews.length === 0 ? (
              <Card className="p-8 text-center">
                <MessageSquare className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
                <p className="text-muted-foreground">{t('artist.noReviews', 'No reviews yet')}</p>
              </Card>
            ) : (
              reviews.map((review, i) => (
                <motion.div key={review.review_id || i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}>
                  <Card className="p-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-medium text-sm">{review.user_name || 'Anonymous'}</span>
                          <div className="flex">{renderStars(review.rating)}</div>
                        </div>
                        <p className="text-sm text-muted-foreground">{review.comment}</p>
                      </div>
                      <span className="text-xs text-muted-foreground whitespace-nowrap">
                        {review.created_at ? new Date(review.created_at).toLocaleDateString() : ''}
                      </span>
                    </div>
                  </Card>
                </motion.div>
              ))
            )}
          </motion.div>
        )}

        {/* Portfolio Tab */}
        {activeTab === 'portfolio' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <Card className="p-6">
              <h2 className="font-heading text-xl mb-4">{t('artist.portfolioGallery', 'Portfolio Gallery')}</h2>
              {artist?.portfolio_images?.length > 0 ? (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {artist.portfolio_images.map((img, i) => (
                    <div key={i} className="aspect-square rounded-sm overflow-hidden border border-border">
                      <img src={img} alt={`Portfolio ${i + 1}`} className="w-full h-full object-cover" />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Camera className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
                  <p className="text-muted-foreground mb-4">{t('artist.noPortfolio', 'No portfolio images yet')}</p>
                  <p className="text-xs text-muted-foreground">{t('artist.portfolioHint', 'Upload your best work to attract clients')}</p>
                </div>
              )}
            </Card>
          </motion.div>
        )}
      </main>
    </div>
  );
};

export default ArtistDashboard;
